import { ChevronRight, Eye, EyeOff, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";

import { AsyncBoundary, EmptyState } from "@/components/data-state";
import { DeleteDialog } from "@/components/delete-dialog";
import { Badge } from "@/components/ui/badge";
import { CreateChallengeDialog } from "@/routes/admin/create-challenge-dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAsync } from "@/hooks/use-async";
import { useAdminCourse, useAdminKey } from "@/hooks/use-auth";
import type { AdminChallenge } from "@/lib/api";
import { ApiError, adminApi } from "@/lib/api";
import { ReadabilityCell } from "@/legacy/readability-cell";
import { formatDeadline, formatTimestamp } from "@/lib/format";

/**
 * Where a challenge runs, and what that costs a student.
 *
 * Worth a column of its own because the two modes have nothing in common from a student's
 * point of view -- one is "download a maze, upload a recording", the other is "open a
 * session" -- and because the mode cannot be changed, so noticing a wrong one early is the
 * difference between deleting a challenge and recreating a term's worth of scores.
 */
function ModeCell({ challenge }: { challenge: AdminChallenge }) {
  if (challenge.simulator === "client") {
    return (
      <span
        className="text-muted-foreground text-xs"
        title="The student runs vis_nav_game locally and uploads a recording."
      >
        Student's machine
      </span>
    );
  }
  return (
    <div className="text-xs">
      <span>Server</span>
      <span className="text-muted-foreground">
        {" · "}
        {challenge.max_attempts === null
          ? "unlimited"
          : `${challenge.max_attempts} attempt${challenge.max_attempts === 1 ? "" : "s"}`}
        {" · "}
        {challenge.max_steps.toLocaleString()} steps
      </span>
    </div>
  );
}

function DeleteChallengeDialog({
  challenge,
  onClose,
  onDeleted,
}: {
  challenge: AdminChallenge | null;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const adminKey = useAdminKey();
  // Ask the server what would be destroyed. It answers 428 with a count, which is far
  // more informative than a generic "are you sure?".
  const challengeId = challenge?.id;
  const preview = useAsync(
    challengeId && adminKey ? () => adminApi.previewDeleteChallenge(challengeId, adminKey) : null,
    [challengeId, adminKey],
  );

  return (
    <DeleteDialog
      open={challenge !== null}
      onOpenChange={(next) => !next && onClose()}
      title={`Delete ${challenge?.name ?? ""}?`}
      description={preview.data ?? "This permanently deletes the challenge and every recording submitted to it."}
      loadingDescription={preview.loading}
      confirmToken={challenge?.id ?? ""}
      onConfirm={async () => {
        if (!adminKey || !challenge) return;
        await adminApi.deleteChallenge(challenge.id, adminKey);
        toast.success("Challenge deleted");
      }}
      onDone={onDeleted}
    />
  );
}

export function AdminChallengesRoute() {
  const adminKey = useAdminKey();
  const courseId = useAdminCourse();
  const [createOpen, setCreateOpen] = useState(false);
  const [deleting, setDeleting] = useState<AdminChallenge | null>(null);

  // Scoped to the selected course, like Students and Teams. Listing every
  // course's challenges together contradicted the selector sitting above the
  // table, and would show several similarly-named ones once a second cohort
  // exists.
  const state = useAsync(
    adminKey && courseId ? () => adminApi.challenges(adminKey, courseId) : null,
    [adminKey, courseId],
  );

  async function patch(
    id: string,
    body: Parameters<typeof adminApi.patchChallenge>[1],
  ) {
    if (!adminKey) return;
    try {
      await adminApi.patchChallenge(id, body, adminKey);
      state.refetch();
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "Failed");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        {/* Disabled rather than silently doing nothing when no course is
            selected, which is what happened when only the submit handler
            guarded on it. */}
        <Button onClick={() => setCreateOpen(true)} disabled={!courseId}>
          <Plus className="size-4" aria-hidden />
          New challenge
        </Button>
      </div>

      <CreateChallengeDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={state.refetch}
      />

      <AsyncBoundary
        state={state}
        empty={<EmptyState title="No challenges yet" />}
      >
        {(list) => {
          // Only a client-simulated challenge has an encrypted file that a build can or
          // cannot read; a term without one has nothing to put in this column.
          const anyClient = list.some((c) => c.simulator === "client");
          return (
          <div className="overflow-hidden rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40">
                  <TableHead>Challenge</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Runs</TableHead>
                  <TableHead>Deadline</TableHead>
                  {anyClient ? <TableHead>Readable</TableHead> : null}
                  <TableHead className="text-center">Reveal</TableHead>
                  <TableHead className="text-center">Active</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...list]
                  .sort((a, b) => b.created_at - a.created_at)
                  .map((challenge) => (
                    <TableRow key={challenge.id} className="group/row">
                      <TableCell>
                        {/* The name opens the challenge's page: every setting, and what
                            students did with it. The row keeps only the two quick toggles
                            and delete, so a TA reading the table is not reading icons. */}
                        <Link
                          to={`/admin/challenges/${challenge.id}`}
                          className="font-medium underline-offset-4 hover:underline"
                        >
                          {challenge.name}
                        </Link>
                        <p className="text-muted-foreground font-mono text-xs">
                          {challenge.id} · created{" "}
                          {formatTimestamp(challenge.created_at)}
                        </p>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            challenge.type === "competition"
                              ? "default"
                              : "secondary"
                          }
                        >
                          {challenge.type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <ModeCell challenge={challenge} />
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDeadline(challenge.deadline)}
                      </TableCell>
                      {anyClient ? (
                        <TableCell>
                          <ReadabilityCell challenge={challenge} />
                        </TableCell>
                      ) : null}
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label="Toggle trajectory visibility"
                          onClick={() =>
                            patch(challenge.id, {
                              show_trajectory: !challenge.show_trajectory,
                            })
                          }
                        >
                          {challenge.show_trajectory ? (
                            <Eye className="size-4" aria-hidden />
                          ) : (
                            <EyeOff className="text-muted-foreground size-4" aria-hidden />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell className="text-center">
                        <Switch
                          checked={challenge.active}
                          onCheckedChange={(active) =>
                            patch(challenge.id, { active })
                          }
                          aria-label={`Toggle ${challenge.name}`}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label="Delete"
                            onClick={() => setDeleting(challenge)}
                          >
                            <Trash2 className="size-4" aria-hidden />
                          </Button>
                          <Link
                            to={`/admin/challenges/${challenge.id}`}
                            aria-label={`Open ${challenge.name}`}
                            className="text-muted-foreground hover:text-foreground inline-flex size-7 items-center justify-center rounded-md"
                          >
                            <ChevronRight className="size-4" aria-hidden />
                          </Link>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
          );
        }}
      </AsyncBoundary>


      <DeleteChallengeDialog
        challenge={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={state.refetch}
      />
    </div>
  );
}
